import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Marquee from "../components/Marquee";
import WorkCard from "../components/WorkCard";
import { FadeUp, MaskedLine, Stagger, StaggerItem } from "../components/Reveal";
import {
  heroLines,
  heroSupport,
  heroDisciplines,
  marqueeBrands,
  aboutBio,
  positioningHeadline,
  positioningCopy,
  stages,
  caseStudies,
  site,
  closingCta,
} from "../data/content";

const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 110]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section className="relative flex min-h-[94vh] flex-col justify-between pt-32 md:pt-40">
      <motion.div style={{ y, opacity }} className="mx-auto w-full max-w-7xl px-6">
        <div className="flex items-center justify-between border-b border-line pb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
          <span>Portfolio — Brand &amp; Experience Strategy</span>
          <span className="hidden md:inline">{site.company}</span>
        </div>

        <h1 className="mt-12 md:mt-16" data-testid="hero-headline">
          {heroLines.map((line, i) => (
            <MaskedLine key={line} delay={0.2 + i * 0.13}>
              <span
                className={`font-display font-extrabold leading-[0.94] tracking-[-0.035em] text-[clamp(3.2rem,10.5vw,9.5rem)] ${
                  i === 2 ? "text-blue" : "text-ink"
                }`}
              >
                {line}
              </span>
            </MaskedLine>
          ))}
        </h1>

        <div className="mt-14 grid gap-10 pb-20 md:mt-20 md:grid-cols-12 md:pb-24">
          <motion.div
            className="md:col-span-5"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-base leading-relaxed text-smoke md:text-lg">
              {heroSupport}
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-smoke/80">
              {heroDisciplines}
            </p>
          </motion.div>
          <motion.div
            className="flex flex-wrap items-end gap-4 md:col-span-7 md:justify-end"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link
              to="/work"
              data-testid="hero-view-work-cta"
              className="group inline-flex items-center gap-2 bg-blue px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-ink"
            >
              View Selected Work
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-about-cta"
              className="inline-flex items-center gap-2 border border-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              About Me
              <ArrowUpRight size={14} />
            </Link>
          </motion.div>
        </div>
      </motion.div>
      <Marquee items={marqueeBrands} />
    </section>
  );
};

const StrategistIntro = () => (
  <section className="border-b border-line" data-testid="strategist-intro">
    <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 md:grid-cols-12 md:py-32">
      <div className="md:col-span-4">
        <FadeUp>
          <div className="relative aspect-[3/4] overflow-hidden border border-line bg-fog">
            <img
              src="/images/arush-portrait.webp"
              alt="Arush Tandon — Brand & Experience Strategist"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-smoke">
            {site.location} · Working across Bharat
          </p>
        </FadeUp>
      </div>
      <div className="md:col-span-7 md:col-start-6">
        <FadeUp>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue">
            The strategist
          </p>
          <h2 className="mt-6 font-display font-bold leading-[1.02] tracking-tight text-[clamp(2rem,4.5vw,4rem)]">
            <MaskedLine animate={false}>ARUSH TANDON.</MaskedLine>
            <MaskedLine animate={false} delay={0.1}>
              <span className="text-smoke">BRAND &amp; EXPERIENCE</span>
            </MaskedLine>
            <MaskedLine animate={false} delay={0.2}>
              <span className="text-smoke">STRATEGIST.</span>
            </MaskedLine>
          </h2>
        </FadeUp>
        <FadeUp delay={0.15}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
            {aboutBio[0]}
          </p>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
            {aboutBio[1]}
          </p>
        </FadeUp>
        <FadeUp delay={0.25}>
          <p className="mt-8 border-l-2 border-blue pl-5 font-mono text-[11px] uppercase leading-relaxed tracking-[0.15em] text-ink">
            Campaigns recognised at ET Brand Equity Brand Disruption Awards, Flame Awards Asia, Eventex — and the India &amp; Asia Book of Records.
          </p>
          <Link
            to="/about"
            data-testid="intro-about-cta"
            className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:text-blue"
          >
            More about me
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>
    </div>
  </section>
);

const Positioning = () => (
  <section className="border-b border-line">
    <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
      <div className="grid gap-14 md:grid-cols-12">
        <div className="md:col-span-7">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue">
            The method
          </p>
          <h2 className="mt-6 font-display font-bold leading-[1.02] tracking-tight text-[clamp(2rem,4.5vw,4rem)]">
            {positioningHeadline.map((line, i) => (
              <MaskedLine key={line} animate={false} delay={i * 0.1}>
                <span className={i === 1 ? "text-blue" : ""}>{line}</span>
              </MaskedLine>
            ))}
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-14">
          {positioningCopy.map((p, i) => (
            <FadeUp key={i} delay={0.1 * i}>
              <p
                className={`mb-6 leading-relaxed ${
                  i === 0
                    ? "font-display text-xl font-semibold tracking-tight text-ink md:text-2xl"
                    : "text-sm text-smoke md:text-base"
                }`}
              >
                {p}
              </p>
            </FadeUp>
          ))}
        </div>
      </div>

      <Stagger className="mt-20 grid border-t border-l border-line sm:grid-cols-2 lg:grid-cols-4">
        {stages.map((s) => (
          <StaggerItem
            key={s.n}
            className="group border-b border-r border-line p-8 transition-colors duration-300 hover:bg-fog md:p-10"
          >
            <span className="font-display text-5xl font-extrabold tracking-tight text-blue md:text-6xl">
              {s.n}
            </span>
            <h3 className="mt-8 font-mono text-xs font-medium uppercase tracking-[0.2em] text-ink">
              {s.title}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-smoke">{s.text}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const SelectedWork = () => (
  <section className="bg-fog/60">
    <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
      <div className="flex flex-wrap items-end justify-between gap-6 border-b border-ink pb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blue">
            Selected work
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-[clamp(2rem,4.5vw,4rem)]">
            TEN PROBLEMS,
            <br />
            TEN PLATFORMS.
          </h2>
        </div>
        <Link
          to="/work"
          data-testid="selected-work-all-link"
          className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 hover:text-blue"
        >
          Full index
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>

      <div className="mt-16 grid gap-x-10 gap-y-20 md:grid-cols-2">
        {caseStudies.map((study, i) => (
          <FadeUp key={study.slug} className={i % 2 === 1 ? "md:translate-y-16" : ""}>
            <WorkCard study={study} index={i} />
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Closing = () => (
  <section className="border-t border-line">
    <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
      <FadeUp>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-smoke">
          One last thought
        </p>
        <h2 className="mt-6 max-w-4xl font-display font-bold leading-[1.02] tracking-tight text-[clamp(2rem,5vw,4.5rem)]">
          {closingCta.headline}
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-smoke md:text-lg">
          {closingCta.sub}
        </p>
        <Link
          to="/contact"
          data-testid="closing-contact-cta"
          className="group mt-10 inline-flex items-center gap-2 bg-ink px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-blue"
        >
          Get in touch
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <StrategistIntro />
      <Positioning />
      <SelectedWork />
      <Closing />
    </>
  );
}
